import { useFormContext } from 'react-hook-form'

import type {
	Budget as BudgetType,
	CategoryIncome,
	Category as CategoryType,
	Expense,
	Income,
} from '@subaio/api-types/budget'
import { ChevronFlip, FormattedMessage } from '@subaio/components'

import { ServiceType } from 'enums'

import { collectCategories } from 'lib/collectCategories'

import { Cell } from 'components/Lists'

import { Amount } from './Amount'
import { Category } from './Category'
import { Container } from './Container'
import { EmptyChevron } from './EmptyChevron'
import { HeaderRow } from './HeaderRow'
import { ItemExpandable } from './ItemExpandable'
import { Subcategory } from './Subcategory'
import { Uncategorized } from './Uncategorized'

type Props = {
	amountCategorized: number
	amountCategorizedUserProvided: number | null
	categories: Array<CategoryType>
	currency: string
	disposableIncome: number
	disposableIncomeUserProvided: number | null
	fieldsBeingSubmitted: Array<string>
	income: Array<CategoryIncome>
	totalMonthlyIncome: number
	totalMonthlyIncomeUserProvided: number | null
	uncategorized: Array<Expense>
	uncategorizedIncome: Array<Income> | null

	isUncategorizedEnabled: boolean
	isUncategorizedIncomeEnabled: boolean

	onRecategorizeUncategorizedService: (args: {
		type: ServiceType
		serviceId: string
		categoryId: string
		subCategoryId: string
	}) => Promise<BudgetType>
}

export const Budget = ({
	categories,
	disposableIncome,
	totalMonthlyIncome,
	amountCategorized,
	disposableIncomeUserProvided,
	uncategorized,
	uncategorizedIncome,
	isUncategorizedEnabled,
	isUncategorizedIncomeEnabled,
	totalMonthlyIncomeUserProvided,
	amountCategorizedUserProvided,
	income,
	currency,
	fieldsBeingSubmitted,
	onRecategorizeUncategorizedService,
}: Props) => {
	const hasIncome = income && income.length > 0
	const hasExpenses = categories && categories.length > 0
	const { reset } = useFormContext()

	const categoriesMap = categories.flatMap(({ id, name, categories }) =>
		categories.map((subCategory) => ({
			label: `${name} - ${subCategory.name}`,
			value: { id, subCategoryId: subCategory.id },
		})),
	)

	const incomeCategoriesMap = income.map(({ id, name }) => ({ label: name, value: { id, subCategoryId: id } }))

	return (
		<Container>
			<div>
				<Uncategorized
					type={ServiceType.Income}
					uncategorized={uncategorizedIncome}
					currency={currency}
					categoriesMap={incomeCategoriesMap}
					isEnabled={isUncategorizedIncomeEnabled}
					onRecategorize={async (props) => {
						const adjustedBudget = await onRecategorizeUncategorizedService({ type: ServiceType.Income, ...props })
						reset({ categories: collectCategories(adjustedBudget) })
					}}
				/>
				<Uncategorized
					type={ServiceType.Expense}
					uncategorized={uncategorized}
					currency={currency}
					categoriesMap={categoriesMap}
					isEnabled={isUncategorizedEnabled}
					onRecategorize={async (props) => {
						const adjustedBudget = await onRecategorizeUncategorizedService({ type: ServiceType.Expense, ...props })
						reset({ categories: collectCategories(adjustedBudget) })
					}}
				/>
			</div>
			<ItemExpandable
				isDisabled={!hasIncome}
				renderRow={({ isExpanded, ...rest }) => (
					<HeaderRow {...rest}>
						<Cell textAlign='center'>{hasIncome ? <ChevronFlip isActive={isExpanded} /> : <EmptyChevron />}</Cell>
						<Cell shouldExpand>
							<FormattedMessage id='flow.budgetEditForm.incomeTitle' defaultMessage='Income after tax' />
						</Cell>
						<Cell>
							<Amount
								amount={totalMonthlyIncome}
								currency={currency}
								userProvidedAmount={totalMonthlyIncomeUserProvided}
								placeholderWidth='90px'
								isLoading={fieldsBeingSubmitted.some((id) => income.some((income) => id.includes(income.id)))}
								type={(totalMonthlyIncomeUserProvided ?? totalMonthlyIncome) === 0 ? 'neutral' : 'positive'}
							/>
						</Cell>
					</HeaderRow>
				)}
			>
				{income.map((income) => (
					<Subcategory key={income.id} fieldsBeingSubmitted={fieldsBeingSubmitted} {...income} currency={currency} />
				))}
			</ItemExpandable>
			<ItemExpandable
				isDisabled={!hasExpenses}
				renderRow={({ isExpanded, ...rest }) => (
					<HeaderRow {...rest}>
						<Cell textAlign='center'>{hasExpenses ? <ChevronFlip isActive={isExpanded} /> : <EmptyChevron />}</Cell>
						<Cell shouldExpand>
							<FormattedMessage id='flow.budgetEditForm.expensesTitle' defaultMessage='Total expenses' />
						</Cell>
						<Cell>
							<Amount
								amount={amountCategorized}
								currency={currency}
								userProvidedAmount={amountCategorizedUserProvided}
								placeholderWidth='80px'
								isLoading={fieldsBeingSubmitted.some((id) => categories.some((category) => id.includes(category.id)))}
								type={(amountCategorizedUserProvided ?? amountCategorized) === 0 ? 'neutral' : 'negative'}
							/>
						</Cell>
					</HeaderRow>
				)}
			>
				{categories.map((category) => (
					<Category key={category.id} fieldsBeingSubmitted={fieldsBeingSubmitted} {...category} currency={currency} />
				))}
			</ItemExpandable>
			<ItemExpandable
				renderRow={(props) => (
					<HeaderRow {...props}>
						<Cell textAlign='center'>
							<EmptyChevron />
						</Cell>
						<Cell shouldExpand>
							<FormattedMessage
								id='flow.budgetEditForm.disposableIncomeTitle'
								defaultMessage='Monthly disposable income'
							/>
						</Cell>
						<Cell>
							<Amount
								amount={disposableIncome}
								currency={currency}
								userProvidedAmount={disposableIncomeUserProvided}
								placeholderWidth='90px'
								isLoading={fieldsBeingSubmitted.length > 0}
								type={(disposableIncomeUserProvided ?? disposableIncome) >= 0 ? 'positive' : 'negative'}
							/>
						</Cell>
					</HeaderRow>
				)}
				isDisabled
			/>
		</Container>
	)
}
